import { Link } from 'react-router-dom';
import { ArrowRight, Rocket, Globe2, Users, Award } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import useSEO from '../hooks/useSEO';
import useJsonLd from '../hooks/useJsonLd';
import { PAGE_SEO, SITE_CONFIG } from '../data/seoConfig';

const VALUES = [
  { icon: Rocket, title: 'Speed over ceremony', desc: 'We ship in weeks, not quarters. Small teams, high ownership.' },
  { icon: Globe2, title: 'Global by design', desc: 'Multi-region, multi-language, multi-currency growth from day one.' },
  { icon: Users, title: 'Senior operators only', desc: 'Every account is led by strategists with 8+ years of experience.' },
  { icon: Award, title: 'Accountable to revenue', desc: 'Weekly numbers. Monthly forecasts. Quarterly board decks.' },
];

const TIMELINE = [
  { year: '2019', label: 'Founded in Toronto', text: 'Ransen begins as a small performance marketing studio for local businesses.' },
  { year: '2021', label: 'Ransen.ca becomes a full agency', text: 'Growth into SEO, analytics, and creative production.' },
  { year: '2023', label: 'AI-first pivot', text: 'Rebuilt around LLMs, server-side tracking, and automation.' },
  { year: '2025', label: 'Global expansion → Ransen.io', text: 'Launched Ransen.io to serve international clients across 4 continents.' },
];

export default function About() {
  useSEO(PAGE_SEO.about);

  useJsonLd('about-breadcrumb', {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_CONFIG.url },
      { '@type': 'ListItem', position: 2, name: 'About', item: `${SITE_CONFIG.url}/about` },
    ],
  });

  return (
    <div>
      <PageHeader
        eyebrow='About Ransen'
        title={<><span className='text-white'>The digital marketing agency</span> <span className='text-brand-gradient'>built for the AI era</span></>}
        description='Ransen is a global AI-powered digital marketing agency. We combine senior strategy, technical tracking, and AI-powered execution to help ambitious companies scale.'
        crumbs={[{ label: 'About' }]}
      />

      <section className='py-16'>
        <div className='max-w-5xl mx-auto px-6'>
          <div className='grid md:grid-cols-2 gap-10 items-start'>
            <div>
              <h2 className='font-heading text-3xl md:text-4xl font-bold text-white leading-tight'>Two brands. One team. Global reach.</h2>
              <p className='mt-5 text-white/70 text-[15.5px] leading-relaxed'>
                Ransen.io is the global counterpart to ransen.ca — our original Canadian brand. Same senior operators, same rigor, expanded to serve clients across North America, Europe, the Middle East, and APAC.
              </p>
              <p className='mt-4 text-white/70 text-[15.5px] leading-relaxed'>
                We combine senior marketing strategy, technical tracking expertise, AI-powered optimization, and execution support to help businesses grow with clarity and confidence.
              </p>
            </div>
            <div className='glass-card rounded-2xl p-7'>
              <div className='grid grid-cols-2 gap-6'>
                {[{k:'150+',v:'Global clients'},{k:'38',v:'Countries served'},{k:'42',v:'Team members'},{k:'6',v:'Time zones covered'}].map((s) => (
                  <div key={s.v}>
                    <div className='font-heading text-4xl font-bold text-brand-gradient'>{s.k}</div>
                    <div className='text-white/55 text-[13px] mt-1'>{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='py-16 border-t border-white/5'>
        <div className='max-w-6xl mx-auto px-6'>
          <h3 className='font-heading text-3xl md:text-4xl font-bold text-white text-center'>What we believe</h3>
          <div className='mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4'>
            {VALUES.map((v) => {
              const I = v.icon;
              return (
                <div key={v.title} className='glass-card rounded-2xl p-6'>
                  <div className='w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/10 border border-violet-500/25 flex items-center justify-center text-violet-200'>
                    <I className='w-5 h-5' strokeWidth={1.75}/>
                  </div>
                  <div className='mt-5 font-semibold text-white text-[15.5px]'>{v.title}</div>
                  <div className='mt-2 text-white/55 text-[13.5px] leading-relaxed'>{v.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className='py-16 border-t border-white/5'>
        <div className='max-w-4xl mx-auto px-6'>
          <h3 className='font-heading text-3xl md:text-4xl font-bold text-white'>Our story</h3>
          <div className='mt-10 relative pl-8 border-l border-white/10 space-y-10'>
            {TIMELINE.map((t) => (
              <div key={t.year} className='relative'>
                <div className='absolute -left-[38px] top-1 w-4 h-4 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 ring-4 ring-[#07040f]' />
                <div className='text-violet-300 text-sm font-semibold tracking-wider'>{t.year}</div>
                <div className='mt-1 text-white font-heading text-xl font-semibold'>{t.label}</div>
                <div className='mt-1 text-white/60 text-[14.5px] leading-relaxed'>{t.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className='py-20 border-t border-white/5 text-center'>
        <div className='max-w-3xl mx-auto px-6'>
          <h3 className='font-heading text-3xl md:text-4xl font-bold text-white'>Let’s build your growth system</h3>
          <Link to='/contact' className='mt-8 btn-primary text-white font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-2'>
            Book a Strategy Call <ArrowRight className='w-4 h-4'/>
          </Link>
        </div>
      </section>
    </div>
  );
}
